export function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function statCard(label: string, value: string | number, sub?: string): string {
  return `
    <div class="frame-pewter p-4 text-center">
      <p class="text-2xl font-heading text-gold">${esc(String(value))}</p>
      <p class="text-[13px] text-muted mt-1">${esc(label)}</p>
      ${sub ? `<p class="text-[11px] text-muted/60 mt-0.5">${esc(sub)}</p>` : ''}
    </div>`;
}

export function adminTable(headers: string[], rows: string[][]): string {
  if (!rows.length) return emptyState('No records found');
  const head = headers
    .map((h) => `<th class="px-3 py-2 text-left text-[11px] text-gold/60 uppercase tracking-wider font-normal">${esc(h)}</th>`)
    .join('');
  const body = rows
    .map(
      (row) =>
        `<tr class="border-t border-border/30 hover:bg-surface-2/40">${row
          .map((cell) => `<td class="px-3 py-2 text-[13px] text-text">${cell}</td>`)
          .join('')}</tr>`,
    )
    .join('');
  return `
    <div class="frame-inset overflow-x-auto">
      <table class="w-full min-w-[600px]">
        <thead><tr>${head}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>`;
}

export function pagination(current: number, total: number): string {
  if (total <= 1) return '';
  return `
    <div class="flex items-center justify-center gap-3 mt-3">
      <button class="btn btn-stone text-[13px] px-3 py-1.5 ${current <= 1 ? 'opacity-40 pointer-events-none' : ''}"
        data-page="${current - 1}">Prev</button>
      <span class="text-[13px] text-muted">${current} / ${total}</span>
      <button class="btn btn-stone text-[13px] px-3 py-1.5 ${current >= total ? 'opacity-40 pointer-events-none' : ''}"
        data-page="${current + 1}">Next</button>
    </div>`;
}

export function searchBar(placeholder: string, id: string): string {
  return `
    <div class="relative">
      <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
      </svg>
      <input id="${esc(id)}" type="text" placeholder="${esc(placeholder)}"
        class="w-full bg-surface-2 border border-border rounded text-text text-[13px] min-h-11 pl-9 pr-3 outline-none focus:border-gold" />
    </div>`;
}

const BADGE_COLORS: Record<string, string> = {
  green: 'bg-emerald-900/60 text-emerald-300',
  red: 'bg-red-900/60 text-red-300',
  yellow: 'bg-amber-900/60 text-amber-300',
  blue: 'bg-sky-900/60 text-sky-300',
  gray: 'bg-neutral-700/60 text-neutral-300',
};

export function badge(text: string, color: 'green' | 'red' | 'yellow' | 'blue' | 'gray'): string {
  return `<span class="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${BADGE_COLORS[color]}">${esc(text)}</span>`;
}

export function confirmModal(title: string, body: string, confirmLabel: string, confirmId: string): string {
  return `
    <div class="scrim fixed inset-0 z-50 flex items-center justify-center" id="${esc(confirmId)}-overlay">
      <div class="frame-pewter p-5 max-w-sm w-full mx-4">
        <h3 class="font-heading text-gold text-lg mb-2">${esc(title)}</h3>
        <p class="text-[13px] text-text mb-4">${body}</p>
        <div class="flex gap-2 justify-end">
          <button class="btn btn-stone text-[13px] px-4 py-2" data-dismiss="${esc(confirmId)}">Cancel</button>
          <button class="btn btn-gold text-[13px] px-4 py-2" id="${esc(confirmId)}">${esc(confirmLabel)}</button>
        </div>
      </div>
    </div>`;
}

export function formField(
  label: string,
  id: string,
  type: string,
  value?: string,
  opts?: { placeholder?: string; required?: boolean; rows?: number },
): string {
  const val = value ? ` value="${esc(value)}"` : '';
  const ph = opts?.placeholder ? ` placeholder="${esc(opts.placeholder)}"` : '';
  const req = opts?.required ? ' required' : '';
  const cls = 'bg-surface-2 border border-border rounded text-text text-[13px] min-h-11 px-2.5 w-full outline-none focus:border-gold';

  const input =
    type === 'textarea'
      ? `<textarea id="${esc(id)}" class="${cls} py-2" rows="${opts?.rows ?? 4}"${ph}${req}>${esc(value ?? '')}</textarea>`
      : `<input id="${esc(id)}" type="${esc(type)}" class="${cls}"${val}${ph}${req} />`;

  return `
    <div class="mb-3">
      <label for="${esc(id)}" class="block text-[13px] text-muted mb-1">${esc(label)}</label>
      ${input}
    </div>`;
}

export function adminSelect(
  label: string,
  id: string,
  options: Array<{ value: string; label: string }>,
  selected?: string,
): string {
  const opts = options
    .map((o) => `<option value="${esc(o.value)}"${o.value === selected ? ' selected' : ''}>${esc(o.label)}</option>`)
    .join('');
  return `
    <div class="mb-3">
      <label for="${esc(id)}" class="block text-[13px] text-muted mb-1">${esc(label)}</label>
      <select id="${esc(id)}"
        class="bg-surface-2 border border-border rounded text-text text-[13px] min-h-11 px-2.5 w-full outline-none focus:border-gold">
        ${opts}
      </select>
    </div>`;
}

export function formatDate(iso: string): string {
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export function showConfirm(message: string, onConfirm: () => void | Promise<void>): void {
  const id = 'adm-confirm-' + Date.now();
  const overlay = document.createElement('div');
  overlay.innerHTML = confirmModal('Confirm', message, 'Confirm', id);
  document.body.appendChild(overlay.firstElementChild!);
  const el = document.getElementById(`${id}-overlay`)!;
  el.querySelector(`[data-dismiss="${id}"]`)?.addEventListener('click', () => el.remove());
  document.getElementById(id)?.addEventListener('click', async () => {
    el.remove();
    await onConfirm();
  });
}

export function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function formatCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * Minor units → "80.00 EUR". Every money figure core's admin API returns is
 * in cents; this is the one place that divides, so a widget cannot show the
 * raw 8000 again (2026-10-19). Non-finite input renders as a dash.
 */
export function formatMoney(cents: number | null | undefined, currency = 'EUR'): string {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return '—';
  return `${formatCents(cents)} ${currency}`;
}

export interface PromptOptions {
  title: string;
  /** Trusted HTML shown above the input — escape anything user-supplied. */
  message?: string;
  label: string;
  confirmLabel: string;
  placeholder?: string;
  /** Minimum trimmed length; 0 makes the text optional. */
  minLength?: number;
  maxLength?: number;
  /** Optional select rendered above the text input; its value is passed to onConfirm. */
  choice?: { label: string; options: Array<{ value: string; label: string }>; selected?: string };
}

/**
 * A confirm dialog with a text input — reasons and notes that core records
 * (ban, reject, resolve). Validates length in place so the dialog stays open
 * on a too-short reason instead of silently doing nothing (2026-10-19).
 */
export function showPrompt(
  opts: PromptOptions,
  onConfirm: (text: string, choice: string) => void | Promise<void>,
): void {
  const id = 'adm-prompt-' + Date.now();
  const min = opts.minLength ?? 0;
  const max = opts.maxLength ?? 1000;
  const wrap = document.createElement('div');
  wrap.innerHTML = `
    <div class="scrim fixed inset-0 z-50 flex items-start sm:items-center justify-center pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]" id="${id}-overlay">
      <div class="frame-pewter p-5 max-w-sm w-full mx-4 max-h-[calc(100dvh-2rem)] overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="${id}-title">
        <h3 id="${id}-title" class="font-heading text-gold text-lg mb-2">${esc(opts.title)}</h3>
        ${opts.message ? `<p class="text-[13px] text-text mb-3">${opts.message}</p>` : ''}
        ${opts.choice ? adminSelect(opts.choice.label, `${id}-choice`, opts.choice.options, opts.choice.selected) : ''}
        ${formField(opts.label, `${id}-text`, 'text', '', { placeholder: opts.placeholder, required: min > 0 })}
        <p id="${id}-err" role="alert" class="text-[12px] text-red-400 min-h-4"></p>
        <div class="flex gap-2 justify-end mt-2">
          <button class="btn btn-stone text-[13px] px-4 min-h-11" data-dismiss="${id}">Cancel</button>
          <button class="btn btn-gold text-[13px] px-4 min-h-11" id="${id}">${esc(opts.confirmLabel)}</button>
        </div>
      </div>
    </div>`;
  const el = wrap.firstElementChild as HTMLElement;
  document.body.appendChild(el);
  const input = el.querySelector<HTMLInputElement>(`#${id}-text`)!;
  input.maxLength = max;
  // On touch the focus would pop the soft keyboard over the buttons before
  // the operator has read the message.
  if (!window.matchMedia?.('(pointer: coarse)').matches) input.focus();
  const close = () => { el.remove(); document.removeEventListener('keydown', onKey); };
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
  document.addEventListener('keydown', onKey);
  el.addEventListener('click', (e) => { if (e.target === el) close(); });
  el.querySelector(`[data-dismiss="${id}"]`)?.addEventListener('click', close);
  const submit = async () => {
    const text = input.value.trim();
    if (text.length < min) {
      el.querySelector(`#${id}-err`)!.textContent = `Enter at least ${min} character${min === 1 ? '' : 's'}.`;
      input.focus();
      return;
    }
    const choiceEl = el.querySelector<HTMLSelectElement>(`#${id}-choice`);
    const choice = choiceEl?.value ?? '';
    if (choiceEl && !choice) {
      el.querySelector(`#${id}-err`)!.textContent = 'Choose an option.';
      choiceEl.focus();
      return;
    }
    close();
    await onConfirm(text, choice);
  };
  el.querySelector(`#${id}`)?.addEventListener('click', () => void submit());
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') void submit(); });
}

export function statusBadge(status: string): string {
  const map: Record<string, 'green' | 'red' | 'yellow' | 'blue' | 'gray'> = {
    COMPLETED: 'green', ACTIVE: 'green', APPROVED: 'green',
    PENDING: 'yellow', IN_PROGRESS: 'yellow',
    FAILED: 'red', REJECTED: 'red', BANNED: 'red',
    NONE: 'gray',
  };
  return badge(status, map[status] ?? 'gray');
}

export const paginationMarkup = pagination;

export function emptyState(message: string): string {
  return `
    <div class="frame-inset p-8 text-center">
      <p class="text-muted text-[13px]">${esc(message)}</p>
    </div>`;
}
